import { useCopyToClipboard } from '@anchorage/hooks';
import { Button } from '@latitude/button';
import { Separator } from '@latitude/separator';
import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import { actionVariants } from 'variants/action-variants';
import { separatorVariants } from 'variants/separator-variants';

import { SafeTooltip } from './SafeTooltip';
import { Toast } from './Toast';
import { ToastAction } from './ToastAction';
import { ToastClose } from './ToastClose';
import { ToastDescription } from './ToastDescription';
import { useToasterContext } from './ToasterProvider';
import { ToastIcon } from './ToastIcon';
import { ToastTitle } from './ToastTitle';

export const CopyToolipText =
  'Double-click the text to copy it to your clipboard';

const CopyTimeout = 3000;

/**
 * Renders the content of the toaster component.
 * @returns JSX.Element
 */
export const ToasterContent = () => {
  const { toasts } = useToasterContext();

  const [copyToClipboard, copied] = useCopyToClipboard(CopyTimeout);

  const handleDoubleClick = (textToCopy: string) => {
    copyToClipboard(textToCopy);
  };

  return toasts.map(
    ({
      id,
      title,
      description,
      action,
      link,
      variant = 'info',
      allowCopyToClipboard,
      ...props
    }) => {
      const { href, children: linkText, altText: linkAltText } = link || {};
      const {
        altText: actionAltText,
        fill = 'outline',
        ...buttonProps
      } = action || {};

      const allowCopy = allowCopyToClipboard ?? variant === 'danger';

      const textContent = (
        <>
          {title && <ToastTitle>{title}</ToastTitle>}
          {description && <ToastDescription>{description}</ToastDescription>}
        </>
      );

      return (
        <Toast key={id} variant={variant} {...props}>
          {copied && (
            <span
              // this is a temporary style/component until we have a proper design for this
              // already discussed with design team and we will push it like this for now
              // so the user has a visual feedback that the text was copied
              style={{ background: 'rgba(0, 0, 0, 0.4)' }}
              data-testid="copied-badge"
              className="fade-in-500 absolute left-0 top-0 m-1 rounded p-1 text-[small]"
            >
              Copied
            </span>
          )}
          <ToastIcon variant={variant} />

          <div
            className="w-[calc(100%_-_80px)] overflow-hidden [&>div]:w-full"
            data-testid="toast-text-content"
            onDoubleClick={() => {
              if (!allowCopy) {
                return;
              }
              const textToCopy = [title, description]
                .filter((text) => !!text)
                .join('\n');

              handleDoubleClick(textToCopy);
            }}
          >
            {allowCopy ? (
              <SafeTooltip text={CopyToolipText}>{textContent}</SafeTooltip>
            ) : (
              textContent
            )}
          </div>

          {linkAltText && (
            <ToastAction altText={linkAltText} asChild>
              <Text variant="link" className="shrink-0 whitespace-nowrap">
                <a href={href}>{linkText} </a>
              </Text>
            </ToastAction>
          )}

          {actionAltText && (
            <ToastAction altText={actionAltText} asChild>
              <div>
                <Button
                  variant="neutral"
                  type={fill ?? 'outline'}
                  className={actionVariants({ fill })}
                  size="tiny"
                  {...buttonProps}
                />
              </div>
            </ToastAction>
          )}

          <Separator
            orientation="vertical"
            className={cn(separatorVariants({ variant }), 'h-6')}
          />

          <ToastClose />
        </Toast>
      );
    },
  );
};
